import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Check, X, Plus, Minus, ShoppingBag, Menu, ArrowRight,
  Truck, ShieldCheck, RotateCcw, Lock, Sparkles, Zap, Brain, Moon,
  Dumbbell, Heart, Leaf, FlaskConical, Quote, Instagram, Award, BookOpen, Play, Gift, Mail, Droplet
} from "lucide-react";
import "./App.css";
import { initMarketingPixels, trackPurchase, trackInitiateCheckout, trackLead } from "./marketingPixels";

/* ---------------- REAL DATA ---------------- */

const PRICE = { single: 249, was: 299, duo: 469, duoWas: 598, family: 899, familyWas: 1196 };

const COLOURS = [
  { name: "Platinum Grey", hex: "#B8BEC4", img: "/images/gpa-platinum.webp" },
  { name: "Bondi Blue", hex: "#0085C7", img: "/images/gpa-bondi.webp" },
  { name: "Charcoal Black", hex: "#1C1C1E", img: "/images/gpa-charcoal.webp" },
  { name: "Sterling Silver", hex: "#D9DCDF", img: "/images/gpa-silver.webp" },
];

const NAV = [
  { label: "Shop", href: "#bundles" },
  { label: "Gift cards", href: "#giftcards" },
  { label: "Science", href: "#science" },
  { label: "How it works", href: "#how" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

const STATS = [
  { num: "60%", label: "of your body is water — a 2% deficit measurably impairs cognition.", source: "Journal of Nutrition, 2012" },
  { num: "20%", label: "faster post-workout recovery reported with adequate hydration.", source: "Sports Medicine review" },
  { num: "41%", label: "improvement in sleep disturbances linked to dehydration.", source: "Sleep Foundation" },
];

const BENEFITS = [
  {
    icon: Zap,
    title: "Performance",
    tagline: "Train hard. Recover faster.",
    body: "Molecular hydrogen is studied as a selective antioxidant — neutralising the free radicals produced during training so your body bounces back between sessions.",
    points: ["Post-workout recovery", "Endurance support", "Reduced oxidative stress"],
    img: "/images/ugc-6.webp",
  },
  {
    icon: Droplet,
    title: "Hydration",
    tagline: "Smarter every sip.",
    body: "H₂ is the smallest molecule in the universe — small enough to diffuse through cell membranes alongside the water you're already drinking, supporting cellular hydration.",
    points: ["Cellular-level absorption", "Daily ritual, on demand", "Pure water, no additives"],
    img: "/images/ugc-3.webp",
  },
  {
    icon: Brain,
    title: "Wellness",
    tagline: "Clarity, focus, longevity.",
    body: "H₂ crosses the blood–brain barrier where it's been studied for cognitive support — and is linked in research to reduced inflammation and healthier ageing.",
    points: ["Focus & mental clarity", "Anti-inflammatory support", "Healthy ageing research"],
    img: "/images/ugc-5.webp",
  },
];

const HOW = [
  { n: "01", title: "Fill up", body: "Pour spring, mineral, or purified water into your GoPureAqua bottle.", img: "/images/step-fill.webp" },
  { n: "02", title: "Press & pick a cycle", body: "Tap once for a 5-minute cycle. Tap twice for 10 minutes — up to 6,000 ppb H₂.", img: "/images/step-press.webp" },
  { n: "03", title: "Sip & elevate", body: "Watch the LED light show. Drink fresh hydrogen-rich water within minutes.", video: "/videos/gpa-display-web.mp4", poster: "/videos/gpa-display-poster.jpg" },
];

const COMPARE = [
  { feature: "Molecular hydrogen (H₂)", us: true, alkaline: false, bottled: false, tap: false },
  { feature: "Antioxidant activity", us: "Up to 6,000 ppb", alkaline: "Variable", bottled: false, tap: false },
  { feature: "Single-use plastic free", us: true, alkaline: "Sometimes", bottled: false, tap: true },
  { feature: "Portable", us: true, alkaline: false, bottled: true, tap: false },
  { feature: "No additives", us: true, alkaline: false, bottled: "Varies", tap: true },
];

/* Verbatim from the live site — no photos, no fabricated locations */
const REVIEWS = [
  { name: "Emily R.", text: "I've never felt more energized! The GoPureAqua bottle has transformed my hydration routine. I take it everywhere, and I can truly feel the difference." },
  { name: "Ryan L.", text: "I was a bit skeptical at first, but after just a week with the GoPureAqua bottle, my recovery times have noticeably improved. Definitely recommend it." },
  { name: "Sarah M.", text: "As an active woman, staying hydrated is crucial for me. The hydrogen water makes me feel revitalized after workouts. I love it." },
  { name: "Jake M.", text: "The sleek design and portability make it easy to work hydrogen water into my daily routine. It's my top choice for staying hydrated." },
  { name: "Megan S.", text: "I appreciate the focus on quality and purity. Knowing I'm drinking antioxidant-rich water makes a huge difference in how I feel. Thank you, GoPureAqua." },
];

const STUDIES = [
  { title: "Molecular hydrogen as a preventive and therapeutic medical gas", journal: "Medical Gas Research, 2015", pmid: "26483953", takeaway: "Reviews 321 studies showing H₂ acts as a selective antioxidant against hydroxyl radicals." },
  { title: "Effects of hydrogen-rich water on antioxidative stress and exercise", journal: "Medical Gas Research, 2012", pmid: "23031079", takeaway: "Athletes drinking H₂ water showed reduced blood lactate and improved recovery markers." },
  { title: "Hydrogen-rich water and cognitive performance in older adults", journal: "Scientific Reports, 2017", pmid: "29097701", takeaway: "Daily H₂ water intake was associated with improved quality-of-life measures." },
];

const FAQ = [
  { q: "What exactly is hydrogen water?", a: "Hydrogen water is regular water infused with molecular hydrogen gas (H₂). The GoPureAqua bottle uses SPE+PEM platinum-coated titanium electrodes to electrolyse water, dissolving H₂ at concentrations of up to 6,000 ppb." },
  { q: "How long do the effects last after I make a bottle?", a: "Hydrogen gas dissipates from open water. We recommend drinking your bottle within 15–30 minutes of generation, and in one sitting if possible, for the highest H₂ concentration." },
  { q: "Is it safe? Are there side effects?", a: "Molecular hydrogen has been the subject of over 1,000 peer-reviewed studies with no toxicity reported at consumed concentrations. As with any wellness change, consult your physician if you have a medical condition." },
  { q: "How is this different from alkaline water?", a: "Alkaline water changes pH; hydrogen water adds dissolved H₂ gas. They target different biology. Hydrogen acts as a selective antioxidant; alkalinity does not." },
  { q: "What kind of water should I use?", a: "Spring, mineral, or filtered/purified water work best. Avoid distilled water (too few electrolytes for efficient electrolysis) and avoid sparkling or flavoured water." },
  { q: "How fast is shipping in Australia?", a: "Standard shipping arrives in 5–7 business days; Express in 2–3 business days. International orders are also available — delivery times vary by location. Orders are processed within 1 business day." },
  { q: "What's in the box? Warranty?", a: "Bottle, USB-C charging cable, instruction card, and a 1-year manufacturer warranty. Contact hello@gopureaqua.com for any return enquiries." },
  { q: "Is it dishwasher safe?", a: "No. Hand wash the bottle interior with warm water. The electrode base is electronic and must not be submerged." },
];

const UGC = [
  "/images/ugc-6.webp",
  "/images/ugc-3.webp",
  "/images/ugc-4.webp",
  "/images/ugc-5.webp",
  "/images/ugc-9.webp",
  "/images/ugc-1.webp",
];

const BUNDLES = [
  { id: "single", name: "Single bottle", quantity: 1, price: PRICE.single, was: PRICE.was, perks: ["1× GoPureAqua bottle", "USB-C cable", "1-year warranty"], featured: false },
  { id: "duo", name: "The duo", quantity: 2, price: PRICE.duo, was: PRICE.duoWas, perks: ["2× GoPureAqua bottles", "Home + on-the-go set", "Save A$129", "Free Australian shipping"], featured: true, badge: "Most popular" },
  { id: "family", name: "Family of four", quantity: 4, price: PRICE.family, was: PRICE.familyWas, perks: ["4× GoPureAqua bottles", "Save A$297", "Free express shipping (AU)", "Priority support"], featured: false },
];

const fmt = (v) => `A$${v.toLocaleString("en-AU")}`;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ---------------- CHECKOUT (Stripe) ---------------- */

async function startCheckout({ bundleId, colour, colours, recipient_email, recipient_name, gift_message }) {
  try {
    const res = await fetch(`${API_BASE}/api/checkout/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bundle_id: bundleId,
        origin_url: window.location.origin,
        colour: colour || null,
        colours: colours || null,
        recipient_email: recipient_email || null,
        recipient_name: recipient_name || null,
        gift_message: gift_message || null,
      }),
    });
    if (!res.ok) throw new Error(`Checkout failed (${res.status})`);
    const data = await res.json();
    if (!data.url) throw new Error("No checkout URL");
    // Fire pixel event right before redirect so it gets captured.
    trackInitiateCheckout({ contentIds: bundleId ? [bundleId] : [] });
    window.location.href = data.url;
  } catch (e) {
    console.error(e);
    alert("Sorry — could not start checkout. Please try again, or email hello@gopureaqua.com.");
  }
}

function CheckoutBanner() {
  const [state, setState] = useState({ kind: null, message: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");

    if (checkout === "cancel") {
      setState({ kind: "cancel", message: "Checkout cancelled — your card was not charged." });
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (checkout === "success" && sessionId) {
      setState({ kind: "pending", message: "Confirming your payment…" });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await fetch(`${API_BASE}/api/checkout/status/${sessionId}`);
          const data = await r.json();
          if (data.payment_status === "paid") {
            setState({ kind: "success", message: `Payment confirmed — thank you! We'll email your order shortly.` });
            // Fire marketing pixels for Purchase (idempotent — fbq/ttq guard against double-fires within a session anyway).
            const amountCents = data.amount_total || 0;
            const value = amountCents > 0 ? amountCents / 100 : undefined;
            const bundleId = data.metadata?.bundle_id;
            trackPurchase({
              value,
              currency: (data.currency || "AUD").toUpperCase(),
              contentIds: bundleId ? [bundleId] : [],
            });
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
          if (data.status === "expired") {
            setState({ kind: "cancel", message: "Checkout session expired. Please try again." });
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
        } catch (e) { /* keep polling */ }
        if (attempts < 8) setTimeout(poll, 2000);
        else setState({ kind: "pending", message: "Still processing — please check your email for confirmation." });
      };
      poll();
    }
  }, []);

  if (!state.kind) return null;
  const tone =
    state.kind === "success" ? "bg-[hsl(var(--bondi))] text-white" :
    state.kind === "cancel" ? "bg-[hsl(var(--ink))] text-white" :
    "bg-[hsl(var(--bondi-soft))] text-[hsl(var(--bondi-dark))]";
  return (
    <div className={`${tone} px-4 py-3 text-center text-sm font-medium`} data-testid="checkout-banner">
      {state.message}
      <button onClick={() => setState({ kind: null, message: "" })} className="ml-3 underline opacity-80 hover:opacity-100" data-testid="checkout-banner-dismiss">Dismiss</button>
    </div>
  );
}

/* ---------------- UI ATOMS ---------------- */

/* Track selected colour globally so CTAs anywhere can read it */
let GLOBAL_COLOUR = "Platinum Grey";
const setGlobalColour = (name) => { GLOBAL_COLOUR = name; };
const getGlobalColour = () => GLOBAL_COLOUR;

const Logo = ({ size = 64 }) => (
  <a href="#top" data-testid="brand-logo" className="flex items-center group" aria-label="GoPureAqua — Elevated Hydration">
    <span
      className="inline-flex items-center justify-center rounded-2xl overflow-hidden bg-black ring-1 ring-black/10 transition-transform group-hover:scale-[1.04] shadow-sm"
      style={{ width: size, height: size, padding: 0 }}
    >
      <img
        src="/logo.png"
        alt="GoPureAqua — Elevated Hydration"
        className="w-full h-full object-contain"
        style={{ display: "block" }}
      />
    </span>
    <span className="sr-only">GoPureAqua</span>
  </a>
);

/* ---------------- SECTIONS ---------------- */

function AnnouncementBar() {
  const items = [
    "Standard AU shipping 5–7 business days",
    "Express AU shipping 2–3 business days",
    "International shipping available",
    "1-year warranty included",
    "Pay later with Afterpay & Shop Pay",
  ];
  return (
    <div className="bg-[hsl(var(--ink))] text-white text-xs overflow-hidden" data-testid="announcement-bar">
      <div className="flex marquee-track whitespace-nowrap py-2.5">
        {[...items, ...items, ...items, ...items].map((t, i) => (
          <span key={i} className="px-8 inline-flex items-center gap-2">
            <Sparkles size={12} className="opacity-60" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function TrustBar() {
  const items = [
    { icon: "🇦🇺", label: "Australian Owned" },
    { icon: "↺", label: "30-Day Money Back Guarantee" },
    { icon: "✈", label: "Free Shipping Australia-Wide" },
    { icon: "★", label: "Thousands of Bottles Sold" },
  ];
  return (
    <div
      className="border-b border-[hsl(var(--grey-200))] bg-[hsl(var(--grey-100))]/60"
      data-testid="trust-bar"
    >
      <div className="container-x px-6 md:px-10 lg:px-16 py-3">
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 md:gap-x-10">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-[11px] md:text-xs font-medium tracking-wide text-[hsl(var(--ink-2))]"
              data-testid={`trust-item-${i}`}
            >
              <Check
                size={14}
                strokeWidth={3}
                className="shrink-0 text-[hsl(var(--violet))]"
              />
              <span className="truncate">{it.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Nav({ onCartOpen }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-[hsl(var(--grey-200))]" data-testid="main-nav">
      <div className="container-x flex items-center justify-between px-6 md:px-10 lg:px-16 py-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-medium text-[hsl(var(--ink-2))] hover:text-[hsl(var(--bondi))] transition" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href="#bundles" className="hidden sm:inline-flex btn-accent !py-2.5 !px-5 text-sm" data-testid="nav-shop-cta">
            Shop · {fmt(PRICE.single)}
          </a>
          <button onClick={onCartOpen} className="relative p-2.5 rounded-full hover:bg-[hsl(var(--grey-100))] transition" aria-label="Cart" data-testid="nav-cart">
            <ShoppingBag size={18} />
          </button>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-full hover:bg-[hsl(var(--grey-100))]" aria-label="Menu" data-testid="nav-mobile-toggle">
            {open ? <X size={20}/> : <Menu size={20}/>}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-[hsl(var(--grey-200))] bg-white"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="py-3 text-base font-medium" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>{n.label}</a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  const [selectedColour, setSelectedColour] = useState(0);
  return (
    <section id="top" className="relative overflow-hidden bg-white">
      <div className="container-x grid lg:grid-cols-12 gap-10 px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-20">
        <div className="lg:col-span-6 flex flex-col justify-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="chip chip-accent w-fit" data-testid="hero-pill">
            <Sparkles size={11}/> Up to 6,000 ppb H₂ · ready in 5 minutes
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="h-display mt-6 text-[clamp(2.5rem,5.8vw,5rem)]"
          >
            Hydration designed for <span style={{ color: "hsl(var(--bondi))" }}>modern wellness</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-5 text-lg text-[hsl(var(--grey-600))] max-w-xl"
          >
            Feel energised, focused and refreshed with hydrogen-enriched water at the touch of a button.
          </motion.p>

          {/* Price block */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 flex items-baseline gap-3" data-testid="hero-price"
          >
            <span className="num-display text-4xl">{fmt(PRICE.single)}</span>
            <span className="text-lg text-[hsl(var(--grey-400))] line-through">{fmt(PRICE.was)}</span>
            <span className="chip chip-violet">Save A${PRICE.was - PRICE.single}</span>
          </motion.div>
          <p className="mt-1 text-xs text-[hsl(var(--grey-600))]">or 4 interest-free payments of A${(PRICE.single / 4).toFixed(2)} with Afterpay</p>

          {/* Colour swatches */}
          <div className="mt-7" data-testid="hero-colours">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--grey-600))] mb-3 font-semibold">
              Colour · <span className="text-[hsl(var(--ink))] normal-case tracking-normal font-medium">{COLOURS[selectedColour].name}</span>
            </div>
            <div className="flex gap-2.5">
              {COLOURS.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => { setSelectedColour(i); setGlobalColour(c.name); }}
                  data-testid={`colour-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                  aria-label={c.name}
                  className={`w-10 h-10 rounded-full transition-all border-2 ${selectedColour === i ? "border-[hsl(var(--ink))] scale-110" : "border-[hsl(var(--grey-200))] hover:border-[hsl(var(--grey-400))]"}`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button onClick={() => startCheckout({ bundleId: "single", colour: COLOURS[selectedColour].name })} className="btn-accent" data-testid="hero-cta-primary">
              Shop now · {fmt(PRICE.single)} <ArrowRight size={18}/>
            </button>
            <a href="#science" className="btn-secondary" data-testid="hero-cta-secondary">
              <BookOpen size={16}/> See the science
            </a>
          </motion.div>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[hsl(var(--grey-600))]">
            <div className="flex items-center gap-2" data-testid="hero-rating">
              <div className="flex">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="hsl(var(--bondi))" className="text-[hsl(var(--bondi))]" />)}
              </div>
              <span className="font-semibold text-[hsl(var(--ink))]">Trusted by health-conscious Australians</span>
            </div>
            <span>·</span>
            <span>1-year warranty</span>
            <span>·</span>
            <span>Australian support</span>
          </div>
        </div>

        <div className="lg:col-span-6 relative flex items-center justify-center min-h-[520px]">
          {/* Aurora glow — slowly pulsing */}
          <motion.div
            aria-hidden
            className="absolute inset-0 -z-10"
            animate={{ opacity: [0.55, 0.85, 0.55] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <div
              className="absolute inset-8 rounded-[3rem]"
              style={{
                background:
                  "radial-gradient(50% 50% at 30% 40%, hsl(var(--bondi)/0.28) 0%, transparent 70%), radial-gradient(45% 45% at 75% 60%, hsl(var(--violet)/0.22) 0%, transparent 70%)",
                filter: "blur(10px)",
              }}
            />
          </motion.div>
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-6 rounded-[2rem] bg-[hsl(var(--grey-50))]" />
          </div>

          {/* Light ray sweep */}
          <motion.div
            aria-hidden
            className="absolute inset-0 overflow-hidden rounded-[2rem] pointer-events-none mix-blend-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="absolute -top-1/2 left-1/2 -translate-x-1/2 w-[60%] h-[200%] rotate-12"
              style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)" }}
              animate={{ x: ["-180%", "180%"] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Effervescent bubbles — 16 varied */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[
              { l: 12, s: 6, d: 0, t: 4.5 },
              { l: 22, s: 9, d: 0.8, t: 5.2 },
              { l: 30, s: 4, d: 1.6, t: 3.8 },
              { l: 38, s: 12, d: 2.2, t: 6.0 },
              { l: 46, s: 7, d: 0.4, t: 4.9 },
              { l: 52, s: 5, d: 1.1, t: 4.2 },
              { l: 58, s: 10, d: 1.9, t: 5.6 },
              { l: 64, s: 6, d: 2.6, t: 4.7 },
              { l: 70, s: 8, d: 0.6, t: 5.1 },
              { l: 76, s: 4, d: 1.4, t: 3.9 },
              { l: 82, s: 11, d: 2.0, t: 5.8 },
              { l: 88, s: 5, d: 0.9, t: 4.4 },
              { l: 18, s: 3, d: 3.2, t: 3.6 },
              { l: 42, s: 3, d: 3.8, t: 3.4 },
              { l: 68, s: 3, d: 2.9, t: 3.7 },
              { l: 80, s: 3, d: 4.2, t: 3.5 },
            ].map((b, i) => (
              <span
                key={i}
                className="absolute block rounded-full"
                style={{
                  left: `${b.l}%`,
                  bottom: "8%",
                  width: `${b.s}px`,
                  height: `${b.s}px`,
                  background: i % 3 === 0 ? "hsl(var(--bondi)/0.3)" : "hsl(var(--bondi)/0.18)",
                  boxShadow: i % 3 === 0 ? "0 0 12px hsl(var(--bondi)/0.5), inset 1px 1px 2px rgba(255,255,255,0.6)" : "inset 1px 1px 1px rgba(255,255,255,0.5)",
                  animation: `rise ${b.t}s ease-in ${b.d}s infinite`,
                }}
              />
            ))}
          </div>

          <div className="relative w-[72%] max-w-md aspect-square flex items-center justify-center" data-testid="hero-product-image">
            {/* Pulse ring behind bottle */}
            <motion.div
              aria-hidden
              className="absolute inset-[15%] rounded-full"
              animate={{ scale: [0.9, 1.05, 0.9], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background: "radial-gradient(circle, hsl(var(--bondi)/0.30) 0%, hsl(var(--violet)/0.18) 50%, transparent 75%)",
                filter: "blur(20px)",
              }}
            />

            <AnimatePresence mode="wait">
              <motion.img
                key={selectedColour}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                src={COLOURS[selectedColour].img}
                alt={`GoPureAqua bottle in ${COLOURS[selectedColour].name}`}
                className="absolute inset-0 w-full h-full object-contain"
                style={{ filter: "drop-shadow(0 24px 32px rgb(0 0 0 / 0.18))" }}
              />
            </AnimatePresence>
          </div>

          {/* spec callouts */}
          <div className="hidden md:flex absolute top-10 left-2 bg-white rounded-xl px-4 py-3 border border-[hsl(var(--grey-200))] shadow-sm flex-col">
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--grey-600))] font-semibold">Capacity</span>
            <span className="num-display text-xl mt-0.5">210<span className="text-sm text-[hsl(var(--grey-600))] font-medium ml-1">ml</span></span>
          </div>
          <div className="hidden md:flex absolute top-10 right-2 bg-white rounded-xl px-4 py-3 border border-[hsl(var(--grey-200))] shadow-sm flex-col">
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--grey-600))] font-semibold">H₂ output</span>
            <span className="num-display text-xl mt-0.5">6K<span className="text-sm text-[hsl(var(--grey-600))] font-medium ml-1">ppb</span></span>
          </div>
          <motion.div
            className="hidden md:flex absolute bottom-12 left-1/2 -translate-x-1/2 bg-[hsl(var(--ink))] text-white rounded-xl px-4 py-3 flex-col"
            animate={{ boxShadow: ["0 0 0 0 hsl(var(--violet)/0.7)", "0 0 0 14px hsl(var(--violet)/0)"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          >
            <span className="text-[10px] uppercase tracking-wider opacity-60 font-semibold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--violet))] animate-pulse"/>
              Cycle time
            </span>
            <span className="num-display text-xl mt-0.5">5<span className="text-sm opacity-70 font-medium ml-1">min</span></span>
          </motion.div>
        </div>
      </div>

      {/* trust bar */}
      <div className="hairline">
        <div className="container-x px-6 md:px-10 lg:px-16 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {[
            { Icon: Truck, label: "AU shipping 5–7 days" },
            { Icon: Zap, label: "Express in 2–3 days" },
            { Icon: ShieldCheck, label: "1-year warranty" },
            { Icon: Lock, label: "Secure checkout" },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 justify-center md:justify-start font-medium text-[hsl(var(--ink-2))]">
              <Icon size={16} className="text-[hsl(var(--bondi))]" /> {label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="section bg-[hsl(var(--grey-50))]" data-testid="stats-section">
      <div className="container-x">
        <p className="h-section text-2xl md:text-3xl max-w-3xl text-[hsl(var(--ink))] mb-14">
          Is your water actually <span style={{ color: "hsl(var(--violet))" }}>doing something</span>, or just keeping you alive?
        </p>
        <div className="grid md:grid-cols-3 gap-10 md:gap-16">
          {STATS.map((s, i) => (
            <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <div className="num-display text-7xl md:text-8xl" style={{ color: i === 1 ? "hsl(var(--violet))" : "hsl(var(--bondi))" }}>{s.num}</div>
              <p className="mt-5 text-lg leading-relaxed text-[hsl(var(--ink-2))]">{s.label}</p>
              <p className="mt-2 text-xs uppercase tracking-wider text-[hsl(var(--grey-400))]">Source: {s.source}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatIs() {
  return (
    <section id="science" className="section bg-[hsl(var(--ink))] text-white relative overflow-hidden">
      <div className="absolute -right-32 -top-32 w-[440px] h-[440px] rounded-full" style={{ background: "radial-gradient(circle, hsl(var(--bondi)) 0%, transparent 70%)", opacity: 0.6 }}/>
      <div className="absolute -left-40 bottom-10 w-[380px] h-[380px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--violet)) 0%, transparent 70%)", opacity: 0.35 }}/>
      <div className="container-x relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="chip border-white/20 bg-white/5 text-white">
            <FlaskConical size={11}/> The science
          </span>
          <h2 className="h-display text-4xl md:text-6xl mt-6 text-white">
            Molecular hydrogen is the smallest, most selective antioxidant ever studied.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-xl">
            H₂ is a neutral gas tiny enough to cross cell membranes — including the blood-brain barrier — and selective enough to neutralise the most damaging free radicals while leaving the helpful ones alone. Over 1,000 peer-reviewed studies have explored its role in inflammation, recovery, cognition, and ageing.
          </p>
          <a href="#studies" className="mt-8 inline-flex items-center gap-2 font-semibold text-white ink-link" data-testid="science-link">
            Read the studies <ArrowRight size={16}/>
          </a>
        </div>
        <div className="lg:col-span-6 grid grid-cols-2 gap-3">
          {[
            { stat: "1,000+", label: "peer-reviewed studies" },
            { stat: "6,000 ppb", label: "H₂ concentration" },
            { stat: "2002", label: "first H₂ research" },
            { stat: "30+", label: "conditions studied" },
          ].map((c) => (
            <div key={c.stat} className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <div className="num-display text-3xl md:text-4xl" style={{ color: "hsl(var(--bondi))" }}>{c.stat}</div>
              <div className="mt-2 text-sm text-white/65">{c.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="section bg-white" data-testid="how-section">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="chip chip-accent">How it works</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5">Three taps to hydrogen-rich water.</h2>
          <p className="mt-5 text-lg text-[hsl(var(--grey-600))]">No filters to swap. No subscriptions. No setup. Just water and a button.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {HOW.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="card-flat" data-testid={`how-step-${i+1}`}
            >
              <div className="aspect-square rounded-xl overflow-hidden bg-[hsl(var(--grey-50))] mb-6 relative">
                {s.video ? (
                  <video
                    src={s.video}
                    poster={s.poster}
                    className="w-full h-full object-cover"
                    autoPlay loop muted playsInline preload="metadata"
                    data-testid={`how-video-${i+1}`}
                  />
                ) : (
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy"/>
                )}
              </div>
              <div className="num-display text-4xl text-[hsl(var(--bondi))]">{s.n}</div>
              <h3 className="text-xl font-bold mt-2 tracking-tight">{s.title}</h3>
              <p className="mt-2 text-[hsl(var(--grey-600))]">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoSection() {
  /* Provide your video URLs here. Common formats:
     - Direct mp4 URL (Shopify Files, Vimeo external, S3, etc.)
     - For YouTube/Vimeo embeds, replace <video> with <iframe>
     Once URLs are added, the play button reveals the video. */
  const VIDEOS = [
    {
      id: "demo",
      title: "GoPureAqua in action",
      sub: "5-minute cycle. Hydrogen bubbles. LED display.",
      src: "/videos/gpa-display-web.mp4",
      poster: "/videos/gpa-display-poster.jpg",
    },
    {
      id: "story",
      title: "Why we built it",
      sub: "Engineering hydration that works as hard as you do.",
      src: "/videos/gpa-story-web.mp4",
      poster: "/videos/gpa-story-poster.jpg",
    },
  ];

  const [playing, setPlaying] = useState(null);

  return (
    <section className="section bg-white" data-testid="video-section">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <span className="chip chip-violet"><Play size={11}/> Watch</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5">See it before you sip it.</h2>
          <p className="mt-5 text-lg text-[hsl(var(--grey-600))]">
            One press. Five minutes. Hydrogen-rich water. The whole experience in under a minute.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {VIDEOS.map((v) => (
            <div
              key={v.id}
              className="relative aspect-video rounded-2xl overflow-hidden bg-[hsl(var(--grey-100))] group cursor-pointer ring-1 ring-black/5"
              data-testid={`video-${v.id}`}
              onClick={() => setPlaying(playing === v.id ? null : v.id)}
            >
              <video
                src={v.src}
                poster={v.poster}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay loop muted playsInline preload="metadata"
                controls={playing === v.id}
              />
              {playing !== v.id && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent pointer-events-none"/>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110">
                      <Play size={26} className="text-[hsl(var(--ink))] ml-1" fill="currentColor"/>
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white pointer-events-none">
                    <div className="text-lg md:text-xl font-bold tracking-tight">{v.title}</div>
                    <div className="text-sm text-white/80 mt-1">{v.sub}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-[hsl(var(--grey-600))]">
          Videos auto-preview muted. Click any tile to enable audio and full controls.
        </p>
      </div>
    </section>
  );
}

function Specs() {
  const items = [
    { label: "Capacity", value: "210 ml" },
    { label: "Weight", value: "880 g" },
    { label: "Bottle material", value: "BPA-free food-grade PC" },
    { label: "Shell & base", value: "Aviation aluminium" },
    { label: "Electrodes", value: "SPE & PEM platinum-coated titanium" },
    { label: "H₂ output", value: "Up to 6,000 ppb" },
  ];
  return (
    <section className="section bg-[hsl(var(--grey-50))]" data-testid="specs-section">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <span className="chip">Technical specifications</span>
          <h2 className="h-display text-4xl md:text-5xl mt-5">Engineered to be the bottle you carry every day.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--grey-200))] rounded-2xl overflow-hidden border border-[hsl(var(--grey-200))]">
          {items.map((it) => (
            <div key={it.label} className="bg-white p-6">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--grey-600))] font-semibold">{it.label}</div>
              <div className="mt-1 text-lg font-semibold text-[hsl(var(--ink))]">{it.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section className="section bg-white" data-testid="benefits-section">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="chip chip-violet">The pillars</span>
            <h2 className="h-display text-4xl md:text-6xl mt-5">Three pillars. One bottle.</h2>
            <p className="mt-5 text-base md:text-lg text-[hsl(var(--grey-600))] max-w-xl">
              Built for people who treat their day like an output — and want hydration that keeps up.
            </p>
          </div>
          <p className="max-w-md text-xs text-[hsl(var(--grey-600))]">
            Based on emerging hydrogen-water research. Individual experience varies — these are not medical claims.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {BENEFITS.map(({ icon: Icon, title, tagline, body, points, img }, i) => (
            <motion.article
              key={title}
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-3xl overflow-hidden bg-[hsl(var(--grey-50))] border border-[hsl(var(--grey-200))] hover:border-[hsl(var(--ink))] transition-all"
              data-testid={`pillar-${title.toLowerCase()}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"/>
                <div className="absolute inset-0" style={{ background: i === 1 ? "linear-gradient(180deg, hsl(var(--violet)/0.25) 0%, hsl(var(--ink)/0.55) 100%)" : "linear-gradient(180deg, hsl(var(--bondi)/0.2) 0%, hsl(var(--ink)/0.55) 100%)" }}/>
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center shadow-lg">
                    <Icon size={20} className={i === 1 ? "text-[hsl(var(--violet))]" : "text-[hsl(var(--bondi))]"}/>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">{title}</span>
                </div>
              </div>
              <div className="p-7">
                <h3 className="h-display text-2xl md:text-3xl tracking-tight">{tagline}</h3>
                <p className="mt-3 text-sm text-[hsl(var(--grey-600))] leading-relaxed">{body}</p>
                <ul className="mt-5 space-y-2">
                  {points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-[hsl(var(--ink-2))]">
                      <Check size={14} strokeWidth={3} className={i === 1 ? "text-[hsl(var(--violet))]" : "text-[hsl(var(--bondi))]"}/>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const renderCell = (v) => {
    if (v === true) return <Check size={16} className="text-[hsl(var(--bondi))] mx-auto" />;
    if (v === false) return <X size={16} className="text-[hsl(var(--grey-400))] mx-auto" />;
    return <span className="text-sm font-medium">{v}</span>;
  };
  return (
    <section className="section bg-[hsl(var(--grey-50))]">
      <div className="container-x">
        <div className="max-w-2xl mb-10">
          <span className="chip">The comparison</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5">Not all water is created equal.</h2>
        </div>
        <div className="overflow-x-auto rounded-2xl bg-white border border-[hsl(var(--grey-200))]" data-testid="comparison-table">
          <table className="w-full text-left min-w-[640px]">
            <thead>
              <tr className="border-b border-[hsl(var(--grey-200))]">
                <th className="p-5 font-medium text-xs uppercase tracking-wider text-[hsl(var(--grey-600))]"></th>
                <th className="p-5 text-base font-bold tracking-tight" style={{ color: "hsl(var(--bondi))" }}>GoPureAqua</th>
                <th className="p-5 text-base font-bold tracking-tight text-[hsl(var(--ink-2))]">Alkaline</th>
                <th className="p-5 text-base font-bold tracking-tight text-[hsl(var(--ink-2))]">Bottled</th>
                <th className="p-5 text-base font-bold tracking-tight text-[hsl(var(--ink-2))]">Tap</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => (
                <tr key={row.feature} className={i !== COMPARE.length - 1 ? "border-b border-[hsl(var(--grey-200))]" : ""}>
                  <td className="p-5 font-medium text-[hsl(var(--ink))]">{row.feature}</td>
                  <td className="p-5 text-center bg-[hsl(var(--bondi-soft))]">{renderCell(row.us)}</td>
                  <td className="p-5 text-center">{renderCell(row.alkaline)}</td>
                  <td className="p-5 text-center">{renderCell(row.bottled)}</td>
                  <td className="p-5 text-center">{renderCell(row.tap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function BuyOnce() {
  const cans = [
    "Single-use aluminium",
    "Industry claims typically ~1,200 ppb per can",
    "Recurring orders, delivery, storage",
    "Hundreds of cans per year, per person",
    "Roughly A$2–3 each, every drink",
  ];
  const bottle = [
    "Reusable for years, every day",
    "Up to 6,000 ppb generated on demand",
    "One purchase. No subscription.",
    "Hydrogen water anywhere, anytime",
    "Cents per refill after you own it",
  ];
  return (
    <section className="section bg-[hsl(var(--ink))] text-white relative overflow-hidden" data-testid="buyonce-section">
      <div className="absolute -right-32 top-10 w-[460px] h-[460px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--violet)) 0%, transparent 70%)", opacity: 0.30 }}/>
      <div className="absolute -left-32 -bottom-20 w-[380px] h-[380px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--bondi)) 0%, transparent 70%)", opacity: 0.30 }}/>

      <div className="container-x relative">
        <div className="max-w-3xl">
          <span className="chip border-white/20 bg-white/5 text-white">Buy once · Own it for life</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5 text-white">
            One bottle replaces <span style={{ color: "hsl(var(--violet))" }}>thousands of cans</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl">
            Canned hydrogen water makes promises by the carton. GoPureAqua makes them by the bottle —
            for years, with nothing to throw away.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {/* Cans column */}
          <div className="rounded-3xl bg-white/5 border border-white/10 p-7" data-testid="buyonce-cans">
            <div className="flex items-center justify-between mb-5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/55">Typical canned hydrogen water</span>
              <span className="text-xs font-semibold text-white/40">Ongoing cost</span>
            </div>
            <ul className="space-y-3">
              {cans.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-[15px] text-white/75 leading-relaxed">
                  <X size={16} strokeWidth={2.5} className="text-white/35 mt-0.5 shrink-0"/>
                  {c}
                </li>
              ))}
            </ul>
            <div className="mt-7 pt-5 border-t border-white/10 text-xs text-white/50">
              Drinking 2 cans/day at A$2.50 each = <span className="text-white/80 font-semibold">~A$1,825/year</span>
            </div>
          </div>

          {/* Bottle column */}
          <div className="relative rounded-3xl p-7 overflow-hidden border border-[hsl(var(--violet)/0.4)]"
               style={{ background: "linear-gradient(135deg, hsl(var(--bondi)/0.18) 0%, hsl(var(--violet)/0.22) 100%)" }}
               data-testid="buyonce-bottle">
            <div className="absolute -top-1 -right-1 w-32 h-32 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--violet)/0.55), transparent 70%)", filter: "blur(10px)" }}/>
            <div className="relative flex items-center justify-between mb-5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">GoPureAqua bottle</span>
              <span className="text-xs font-semibold text-[hsl(var(--violet))]">A$249 · once</span>
            </div>
            <ul className="relative space-y-3">
              {bottle.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[15px] text-white leading-relaxed">
                  <Check size={16} strokeWidth={3} className="text-[hsl(var(--violet))] mt-0.5 shrink-0"/>
                  {b}
                </li>
              ))}
            </ul>
            <div className="relative mt-7 pt-5 border-t border-white/15 text-xs text-white/65">
              Pays for itself in roughly <span className="text-white font-semibold">3 months</span> vs. a 2-can/day habit.
            </div>
          </div>
        </div>

        {/* Sustainability stat strip */}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="num-display text-5xl text-[hsl(var(--violet))]">~1,800</div>
            <p className="mt-3 text-sm text-white/65 leading-relaxed">Aluminium cans diverted from landfill in your first year alone (based on 5 refills/day).</p>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="num-display text-5xl text-[hsl(var(--bondi))]">0</div>
            <p className="mt-3 text-sm text-white/65 leading-relaxed">Subscriptions. Deliveries. Empty cans clogging the recycling bin.</p>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="num-display text-5xl text-white">5×</div>
            <p className="mt-3 text-sm text-white/65 leading-relaxed">Higher peak H₂ output than typical canned hydrogen water claims — generated fresh, when you drink it.</p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#bundles" className="btn-accent" data-testid="buyonce-cta">
            Shop the bottle <ArrowRight size={18}/>
          </a>
          <span className="text-sm text-white/55">Free Australian shipping · 1-year warranty · Cents per refill.</span>
        </div>
      </div>
    </section>
  );
}

function Studies() {
  return (
    <section id="studies" className="section bg-white" data-testid="studies-section">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="chip chip-accent"><BookOpen size={11}/> Peer-reviewed</span>
            <h2 className="h-display text-4xl md:text-6xl mt-5">We back what we say with citations.</h2>
          </div>
          <a href="https://pubmed.ncbi.nlm.nih.gov/?term=molecular+hydrogen" target="_blank" rel="noreferrer" className="font-semibold text-sm inline-flex items-center gap-2 ink-link" data-testid="all-studies-link">
            Browse the full library on PubMed <ArrowRight size={16}/>
          </a>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {STUDIES.map((s, i) => (
            <div key={s.title} className="card-flat" data-testid={`study-${i+1}`}>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--grey-400))] font-semibold">{s.journal}</div>
              <h3 className="text-lg font-semibold mt-3 leading-snug tracking-tight">{s.title}</h3>
              <p className="mt-4 text-sm text-[hsl(var(--grey-600))] leading-relaxed">{s.takeaway}</p>
              <a href={`https://pubmed.ncbi.nlm.nih.gov/${s.pmid}/`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--bondi))] ink-link">
                Read on PubMed (PMID {s.pmid}) <ArrowRight size={14}/>
              </a>
            </div>
          ))}
        </div>
        <p className="mt-10 text-xs text-[hsl(var(--grey-400))] max-w-3xl">
          These statements have not been evaluated by the TGA. GoPureAqua products are not intended to diagnose, treat, cure, or prevent any disease.
        </p>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section id="reviews" className="section bg-[hsl(var(--grey-50))]">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <span className="chip"><Award size={11}/> What our customers are saying</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5">Real people. Real hydration.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card-flat" data-testid={`review-${i+1}`}
            >
              <Quote size={20} className="text-[hsl(var(--bondi))] opacity-50 mb-3"/>
              <div className="flex mb-3">
                {[...Array(5)].map((_, j) => <Star key={j} size={13} fill="hsl(var(--bondi))" className="text-[hsl(var(--bondi))]"/>)}
              </div>
              <p className="text-[hsl(var(--ink-2))] leading-relaxed text-[15px]">&ldquo;{r.text}&rdquo;</p>
              <div className="mt-5 font-semibold text-sm">— {r.name}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BundleCard({ b }) {
  /* Mix-and-match: maintain per-bottle colour selection (length = b.quantity) */
  const [picks, setPicks] = useState(() => Array(b.quantity).fill("Platinum Grey"));

  const setPick = (idx, name) => {
    setPicks((cur) => cur.map((c, i) => (i === idx ? name : c)));
  };

  return (
    <div
      className={`relative rounded-2xl p-7 flex flex-col ${b.featured ? "bg-[hsl(var(--ink))] text-white" : "bg-white border border-[hsl(var(--grey-200))]"}`}
      data-testid={`bundle-${b.id}`}
    >
      {b.badge && (
        <div className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[hsl(var(--violet))] text-white shadow-lg shadow-[hsl(var(--violet)/0.35)]">
          {b.badge}
        </div>
      )}
      <h3 className="text-xl font-bold tracking-tight">{b.name}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="num-display text-4xl">{fmt(b.price)}</div>
        <div className={`line-through text-sm ${b.featured ? "text-white/40" : "text-[hsl(var(--grey-400))]"}`}>{fmt(b.was)}</div>
      </div>
      <ul className="mt-5 space-y-2">
        {b.perks.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-sm">
            <Check size={15} className="mt-0.5 shrink-0 text-[hsl(var(--bondi))]"/>
            <span className={b.featured ? "text-white/85" : "text-[hsl(var(--ink-2))]"}>{p}</span>
          </li>
        ))}
      </ul>

      {/* Per-bottle colour selectors — mix & match */}
      <div className={`mt-6 pt-5 border-t ${b.featured ? "border-white/15" : "border-[hsl(var(--grey-200))]"}`} data-testid={`bundle-colours-${b.id}`}>
        <div className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${b.featured ? "text-white/55" : "text-[hsl(var(--grey-600))]"}`}>
          Choose your colour{b.quantity > 1 ? "s" : ""} {b.quantity > 1 && <span className="normal-case tracking-normal font-normal opacity-60">· mix & match</span>}
        </div>
        <div className="space-y-2">
          {picks.map((selected, i) => (
            <div key={i} className="flex items-center gap-2.5" data-testid={`bundle-${b.id}-bottle-${i+1}`}>
              {b.quantity > 1 && (
                <span className={`text-[11px] font-semibold w-14 shrink-0 ${b.featured ? "text-white/65" : "text-[hsl(var(--ink-2))]"}`}>
                  Bottle {i + 1}
                </span>
              )}
              <div className="flex gap-1.5 flex-wrap">
                {COLOURS.map((c) => {
                  const isOn = selected === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setPick(i, c.name)}
                      aria-label={c.name}
                      title={c.name}
                      data-testid={`bundle-${b.id}-bottle-${i+1}-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-7 h-7 rounded-full transition-all ${isOn ? "ring-2 ring-offset-2 scale-110" : "ring-1 ring-black/10 hover:scale-105"}`}
                      style={{
                        background: c.hex,
                        boxShadow: isOn ? `0 0 0 2px ${b.featured ? "#ffffff" : "hsl(var(--ink))"}` : undefined,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className={`mt-3 text-[11px] ${b.featured ? "text-white/55" : "text-[hsl(var(--grey-600))]"}`}>
          Selected: {picks.join(" · ")}
        </div>
      </div>

      <button
        onClick={() => startCheckout({ bundleId: b.id, colours: picks })}
        className={`mt-6 w-full rounded-full py-3 font-semibold transition-all ${
          b.featured
            ? "bg-white text-[hsl(var(--ink))] hover:bg-[hsl(var(--bondi-soft))]"
            : "bg-[hsl(var(--ink))] text-white hover:bg-[hsl(var(--bondi))]"
        }`}
        data-testid={`bundle-cta-${b.id}`}
      >
        Add to cart · {fmt(b.price)}
      </button>
    </div>
  );
}

function Bundles() {
  return (
    <section id="bundles" className="section bg-white">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <span className="chip">Choose your set</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5">Pick the pack — and pick the colours.</h2>
          <p className="mt-5 text-lg text-[hsl(var(--grey-600))]">
            Order one for yourself or mix and match colours for the whole household.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 items-start">
          {BUNDLES.map((b) => <BundleCard key={b.id} b={b} />)}
        </div>
        <p className="mt-6 text-xs text-[hsl(var(--grey-600))]">
          All prices in AUD. Standard Australian shipping 5–7 business days. Express 2–3 days. Secure checkout powered by Stripe.
        </p>
      </div>
    </section>
  );
}

function GiftCards() {
  const GIFT_OPTIONS = [
    { id: "gift_50", amount: 50, blurb: "A taste of pure hydration." },
    { id: "gift_100", amount: 100, blurb: "Stack it toward a single bottle.", popular: false },
    { id: "gift_250", amount: 250, blurb: "Covers a full bottle and then some.", popular: true },
    { id: "gift_500", amount: 500, blurb: "The grand gesture. Enough for two.", popular: false },
  ];

  const [selected, setSelected] = useState("gift_250");
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");

  const picked = GIFT_OPTIONS.find((g) => g.id === selected);

  const submit = (e) => {
    e?.preventDefault?.();
    startCheckout({
      bundleId: selected,
      recipient_email: recipient || null,
      recipient_name: recipientName || null,
      gift_message: message || null,
    });
  };

  return (
    <section id="giftcards" className="section bg-[hsl(var(--ink))] text-white relative overflow-hidden" data-testid="giftcards-section">
      {/* ambient glow */}
      <div className="absolute -right-32 -top-32 w-[440px] h-[440px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--bondi)) 0%, transparent 70%)", opacity: 0.45 }}/>
      <div className="absolute -left-24 bottom-0 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--violet)) 0%, transparent 70%)", opacity: 0.35 }}/>

      <div className="container-x relative grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="chip border-white/20 bg-white/5 text-white"><Gift size={11}/> Gift cards</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5 text-white">Give the gift of elevated hydration.</h2>
          <p className="mt-5 text-white/70 max-w-md">
            A digital gift card emailed directly to the recipient — or to you, to print and hand-deliver. Redeemable on any GoPureAqua bottle or bundle. Never expires.
          </p>

          {/* Animated gift card preview */}
          <motion.div
            key={selected}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-8 relative rounded-2xl p-6 max-w-sm overflow-hidden"
            style={{
              background: "linear-gradient(135deg, hsl(var(--bondi)) 0%, hsl(var(--violet)) 70%, hsl(var(--ink)) 100%)",
              boxShadow: "0 30px 60px -20px hsl(var(--violet)/0.55)",
            }}
            data-testid="giftcard-preview"
          >
            <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white/10"/>
            <div className="absolute -right-2 -bottom-8 w-24 h-24 rounded-full bg-white/5"/>
            <div className="flex items-center justify-between">
              <img src="/logo.png" alt="GoPureAqua" className="h-9 w-9 rounded-lg shadow-lg ring-1 ring-white/30 object-cover" data-testid="giftcard-logo"/>
              <div className="flex items-center gap-1.5 text-white/80 text-[10px] uppercase tracking-[0.2em] font-semibold">
                <Gift size={11}/> Gift Card
              </div>
            </div>
            <div className="mt-10 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Value</div>
                <div className="num-display text-5xl text-white">A${picked.amount}</div>
              </div>
              <div className="text-right text-[10px] uppercase tracking-wider text-white/60">
                Never expires
              </div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          {/* Denominations */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="giftcard-denominations">
            {GIFT_OPTIONS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setSelected(g.id)}
                data-testid={`giftcard-option-${g.amount}`}
                className={`relative rounded-2xl p-5 text-left transition-all border ${
                  selected === g.id
                    ? "bg-white text-[hsl(var(--ink))] border-white"
                    : "bg-white/5 text-white border-white/15 hover:bg-white/10"
                }`}
              >
                {g.popular && (
                  <span className="absolute -top-2.5 right-3 text-[9px] font-bold uppercase tracking-wider bg-[hsl(var(--violet))] text-white px-2 py-0.5 rounded-full shadow-md shadow-[hsl(var(--violet)/0.4)]">
                    Most gifted
                  </span>
                )}
                <div className="num-display text-3xl">A${g.amount}</div>
                <div className={`mt-2 text-xs leading-relaxed ${selected === g.id ? "text-[hsl(var(--ink-2))]" : "text-white/65"}`}>{g.blurb}</div>
              </button>
            ))}
          </div>

          {/* Recipient form */}
          <form onSubmit={submit} className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5 space-y-3" data-testid="giftcard-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/55 font-semibold mb-1.5 block">Recipient name <span className="text-white/30">(optional)</span></span>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Alex Murphy"
                  className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2.5 text-white placeholder:text-white/30 focus:border-[hsl(var(--bondi))] outline-none transition"
                  data-testid="giftcard-recipient-name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/55 font-semibold mb-1.5 block flex items-center gap-1"><Mail size={10}/> Recipient email <span className="text-white/30">(optional)</span></span>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="alex@example.com.au"
                  className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2.5 text-white placeholder:text-white/30 focus:border-[hsl(var(--bondi))] outline-none transition"
                  data-testid="giftcard-recipient-email"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/55 font-semibold mb-1.5 block">Personal message <span className="text-white/30">(optional, max 500 chars)</span></span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Cheers to a year of hydration done right —"
                className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2.5 text-white placeholder:text-white/30 focus:border-[hsl(var(--bondi))] outline-none resize-none transition"
                data-testid="giftcard-message"
              />
            </label>
            <button
              type="submit"
              className="w-full rounded-full bg-[hsl(var(--bondi))] hover:bg-[hsl(var(--bondi-dark))] text-white py-3.5 font-semibold transition flex items-center justify-center gap-2"
              data-testid="giftcard-submit"
            >
              Send A${picked.amount} gift card <ArrowRight size={16}/>
            </button>
            <p className="text-[11px] text-white/45 text-center">
              Leave the recipient blank to email the card to yourself for printing or forwarding.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function UGCStrip() {
  return (
    <section className="section bg-[hsl(var(--grey-50))]">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <h2 className="h-section text-3xl md:text-5xl">
            <Instagram className="inline-block -mt-2 mr-2" size={30}/> @gopureaqua in the wild
          </h2>
          <a href="https://instagram.com/gopureaqua" target="_blank" rel="noreferrer" className="font-semibold text-sm ink-link inline-flex items-center gap-2" data-testid="instagram-link">
            Tag us to be featured <ArrowRight size={14}/>
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {UGC.map((src, i) => (
            <div key={i} className="aspect-square rounded-xl overflow-hidden group relative cursor-pointer bg-[hsl(var(--grey-200))]">
              <img src={src} alt="GoPureAqua community" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                <Instagram size={16} className="text-white"/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="section bg-white">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="chip">Questions</span>
          <h2 className="h-display text-4xl md:text-5xl mt-5">Everything you wanted to know.</h2>
          <p className="mt-5 text-[hsl(var(--grey-600))]">
            Still curious? Email <a href="mailto:hello@gopureaqua.com" className="ink-link font-semibold text-[hsl(var(--ink))]">hello@gopureaqua.com</a>.
          </p>
        </div>
        <div className="lg:col-span-8" data-testid="faq-list">
          {FAQ.map((f, i) => (
            <div key={f.q} className="border-b border-[hsl(var(--grey-200))]">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full text-left py-5 flex items-center justify-between gap-4"
                data-testid={`faq-question-${i+1}`}
                aria-expanded={open === i}
              >
                <span className="text-base md:text-lg font-semibold pr-4 tracking-tight">{f.q}</span>
                <span className="shrink-0 w-8 h-8 rounded-full border border-[hsl(var(--grey-200))] flex items-center justify-center">
                  {open === i ? <Minus size={14}/> : <Plus size={14}/>}
                </span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22 }} className="overflow-hidden" data-testid={`faq-answer-${i+1}`}
                  >
                    <p className="pb-5 text-[hsl(var(--grey-600))] leading-relaxed max-w-2xl">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting || sent) return;
    setError("");
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "newsletter_footer" }),
      });
      if (!r.ok) throw new Error("Subscribe failed");
      trackLead(email);
      setSent(true);
    } catch (err) {
      setError("Hmm, that didn't work. Try again, or email hello@gopureaqua.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section bg-[hsl(var(--ink))] text-white relative overflow-hidden" data-testid="newsletter-section">
      <div className="absolute -left-20 -bottom-32 w-[440px] h-[440px] rounded-full" style={{ background: "radial-gradient(circle, hsl(var(--bondi)) 0%, transparent 70%)", opacity: 0.5 }}/>
      <div className="container-x relative grid md:grid-cols-2 gap-10 items-center">
        <div>
          <span className="chip border-white/20 bg-white/5 text-white">Welcome offer · 10% off</span>
          <h2 className="h-display text-4xl md:text-6xl mt-5 text-white">Get 10% off your first bottle.</h2>
          <p className="mt-5 text-white/65 max-w-md">
            Drop your email for a one-time code, plus monthly hydration science. No fluff, no spam.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-white/5 border border-white/10 rounded-2xl p-2 flex flex-col sm:flex-row gap-2"
          data-testid="newsletter-form"
        >
          <input
            type="email" required placeholder="you@hydrated.com.au"
            value={email} onChange={(e) => setEmail(e.target.value)} disabled={sent || submitting}
            className="flex-1 bg-transparent px-5 py-3.5 outline-none text-white placeholder:text-white/40"
            data-testid="newsletter-email-input"
          />
          <button
            type="submit" disabled={sent || submitting}
            className="rounded-xl bg-[hsl(var(--bondi))] hover:bg-[hsl(var(--bondi-dark))] px-6 py-3.5 font-semibold transition disabled:opacity-60"
            data-testid="newsletter-submit"
          >
            {sent ? "Check your inbox ✓" : submitting ? "Subscribing…" : "Send my code"}
          </button>
        </form>
        {error && (
          <p className="md:col-span-2 text-sm text-[hsl(var(--bondi-soft))]/90" data-testid="newsletter-error">{error}</p>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-white border-t border-[hsl(var(--grey-200))]">
      <div className="container-x px-6 md:px-10 lg:px-16 py-14 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <Logo/>
          <p className="mt-5 text-sm text-[hsl(var(--grey-600))] max-w-sm">
            Portable hydrogen water. Backed by science. Australian-owned and supported, for anyone who wants more from their hydration.
          </p>
          <p className="mt-4 text-xs text-[hsl(var(--grey-600))]">
            <a href="mailto:hello@gopureaqua.com" className="ink-link">hello@gopureaqua.com</a>
          </p>
        </div>
        {[
          { title: "Shop", links: [{ label: "The bottle", href: "#bundles" }, { label: "Bundles", href: "#bundles" }, { label: "Gift cards", href: "#giftcards" }] },
          { title: "Learn", links: [{ label: "The science", href: "#science" }, { label: "Studies", href: "#studies" }, { label: "FAQ", href: "#faq" }] },
          { title: "Company", links: [{ label: "Contact", href: "mailto:hello@gopureaqua.com" }, { label: "Wholesale", href: "mailto:hello@gopureaqua.com?subject=Wholesale" }, { label: "Reviews", href: "#reviews" }] },
        ].map((col) => (
          <div key={col.title}>
            <div className="text-sm font-bold mb-4 tracking-tight">{col.title}</div>
            <ul className="space-y-2.5 text-sm text-[hsl(var(--grey-600))]">
              {col.links.map((l) => <li key={l.label}><a href={l.href} className="ink-link" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>{l.label}</a></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[hsl(var(--grey-200))]">
        <div className="container-x px-6 md:px-10 lg:px-16 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-[hsl(var(--grey-600))]">
          <div>© {new Date().getFullYear()} GoPureAqua · ABN-listed · Built in Australia</div>
          <div className="flex gap-5">
            <a href="#" className="ink-link">Privacy</a>
            <a href="#" className="ink-link">Terms</a>
            <a href="#" className="ink-link">Shipping & returns</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function StickyCTA() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 1200);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-30"
          data-testid="sticky-cta"
        >
          <div className="bg-[hsl(var(--ink))] text-white rounded-xl shadow-2xl flex items-center gap-3 p-2.5 pr-2 max-w-md ml-auto">
            <img src="/images/gpa-platinum.webp" alt="" className="w-11 h-11 object-contain"/>
            <div className="flex-1">
              <div className="text-[11px] opacity-65">GoPureAqua bottle</div>
              <div className="font-semibold text-sm">{fmt(PRICE.single)} <span className="text-[11px] opacity-60 line-through ml-1">{fmt(PRICE.was)}</span></div>
            </div>
            <button onClick={() => startCheckout({ bundleId: "single", colour: getGlobalColour() })} className="rounded-full bg-[hsl(var(--violet))] hover:bg-[hsl(var(--violet-dark))] px-4 py-2 text-sm font-semibold shadow-lg shadow-[hsl(var(--violet)/0.4)] transition" data-testid="sticky-cta-button">
              Add to cart
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------------- APP ---------------- */

export default function App() {
  const [cartOpen, setCartOpen] = useState(false);
  useEffect(() => { initMarketingPixels(); }, []);
  return (
    <div className="min-h-screen bg-white text-[hsl(var(--ink))]">
      <CheckoutBanner/>
      <AnnouncementBar/>
      <Nav onCartOpen={() => setCartOpen(true)} />
      <TrustBar/>
      <Hero/>
      <Benefits/>
      <Stats/>
      <WhatIs/>
      <HowItWorks/>
      <VideoSection/>
      <Specs/>
      <Comparison/>
      <BuyOnce/>
      <Studies/>
      <Reviews/>
      <Bundles/>
      <GiftCards/>
      <UGCStrip/>
      <FAQSection/>
      <Newsletter/>
      <Footer/>
      <StickyCTA/>

      <AnimatePresence>
        {cartOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40" onClick={() => setCartOpen(false)} data-testid="cart-overlay"
          >
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28 }}
              className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()} data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="text-xl font-bold tracking-tight">Your cart</div>
                <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-close"><X size={20}/></button>
              </div>
              <div className="text-center py-14 text-[hsl(var(--grey-600))]">
                <ShoppingBag size={36} className="mx-auto mb-3 opacity-40"/>
                <div>Your cart is empty.</div>
                <button onClick={() => { setCartOpen(false); document.getElementById('bundles')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-accent mt-6 inline-flex">Shop the bottle</button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
